import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { AumicoClipboardService, FormStatus, USER_INPUT_DEBOUNCE_TIME } from '@modeso/aumico-lib-core-fe';
import { AumicoClipboardData } from '@modeso/aumico-lib-core-fe/src/lib/interfaces/aumico-clipboard-data.interface';
import { ComponentType } from '@modeso/types__aumico-lib-notes-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AddNoteAccordionResult } from '../../interfaces/add-note-accordion-result.interface';
import { NoteSelectionCard } from '../../interfaces/note-selection-card.interface';

@UntilDestroy()
@Component({
    selector: 'add-notes-accordion-section',
    templateUrl: './add-notes-accordion-section.component.html',
    styleUrls: ['./add-notes-accordion-section.component.scss'],
})
export class AddNotesAccordionSectionComponent implements OnInit {
    @Input() supportOperation = false;
    @Output() noteAccordionAdded = new EventEmitter<AddNoteAccordionResult>();

    @Output() close = new EventEmitter<void>();

    selectedNoteComponent: ComponentType | undefined;
    formGroup: FormGroup;
    private addedNote: AddNoteAccordionResult | undefined;
    public isFormValid = false;

    showClipboard = false;
    pasteComponentSelected = false;
    clipboardData: AumicoClipboardData | undefined;

    componentInClipboardImagePath: string | undefined;

    constructor(private formBuilder: FormBuilder, private readonly aumicoClipboardService: AumicoClipboardService) {
        this.aumicoClipboardService
            .tryReadAumicoClipboard$()
            .pipe(untilDestroyed(this))
            .subscribe((clipboardData) => {
                if (clipboardData) {
                    this.showClipboard = true;
                    this.clipboardData = clipboardData;

                    this.componentInClipboardImagePath = this.notesComponents.find(
                        (c) => c.componentName === clipboardData.componentType,
                    )?.imagePath;
                }
            });
    }

    readonly notesComponents: NoteSelectionCard[] = [
        {
            title: 'aumico_notes_rich_text',
            imagePath: 'assets/images/aumico-rich-text-card-popup.svg',
            componentName: ComponentType.RICH_TEXT,
            shouldBeVisibleForUser: true,
        },
        {
            title: 'aumico_notes_simple_input',
            imagePath: 'assets/images/aumico-input-card-popup.svg',
            componentName: ComponentType.INPUT,
            shouldBeVisibleForUser: false,
        },
        {
            title: 'aumico_notes_super_table',
            imagePath: 'assets/images/aumico-super-table-card-popup1.svg',
            componentName: ComponentType.SUPER_TABLE,
            shouldBeVisibleForUser: true,
        },
        {
            title: 'aumico_notes_select',
            imagePath: 'assets/images/aumico-select-card-popup.svg',
            componentName: ComponentType.SELECT,
            shouldBeVisibleForUser: false,
        },
        {
            title: 'aumico_notes_movement_schedule',
            imagePath: 'assets/images/aumico-movement-schedule-card-popup.svg',
            componentName: ComponentType.MOVEMENT_SCHEDULE,
            shouldBeVisibleForUser: true,
        },
        {
            title: 'aumico_notes_free_table_with_amounts',
            imagePath: 'assets/images/aumico-freetable-card-popup.svg',
            componentName: ComponentType.FREE_TABLE_WITH_AMOUNTS,
            shouldBeVisibleForUser: true,
        },
        {
            title: 'aumico_notes_fli_table',
            imagePath: 'assets/images/aumico-fli-card-popup.svg',
            componentName: ComponentType.FINANCIAL_LINE_ITEM,
            shouldBeVisibleForUser: true,
        },
        {
            title: 'aumico_notes_equity_table',
            imagePath: 'assets/images/aumico-equity-card-popup.svg',
            componentName: ComponentType.EQUITY,
            shouldBeVisibleForUser: false,
        },
    ];

    ngOnInit(): void {
        this.initializeForm();
        this.monitorFormChanges();
    }

    private initializeForm() {
        this.formGroup = this.formBuilder.group({
            title: new UntypedFormControl('', [Validators.required, this.notEmptyValidator]),
            addNumbering: new UntypedFormControl(false),
            selectedComponent: new UntypedFormControl(undefined, [Validators.required]),
        });
    }

    private notEmptyValidator(control: UntypedFormControl): ValidationErrors | null {
        if (control.value?.toString().trim() === '') {
            return { required: true };
        }
        return null;
    }

    private monitorFormChanges(): void {
        this.formGroup.valueChanges.pipe(debounceTime(USER_INPUT_DEBOUNCE_TIME), untilDestroyed(this)).subscribe(() => {
            const noteTitle = this.formGroup?.get('title')?.value as unknown;

            if (noteTitle == null || typeof noteTitle !== 'string') {
                throw new Error('Title is undefined or not a string');
            }

            this.addedNote = {
                title: noteTitle.trim(),
                addNumbering: this.formGroup?.get('addNumbering')?.value ?? false,
                selectedComponent: this.selectedNoteComponent,
            };
        });

        this.formGroup.statusChanges
            .pipe(debounceTime(USER_INPUT_DEBOUNCE_TIME), distinctUntilChanged(), untilDestroyed(this))
            .subscribe((status) => {
                this.isFormValid = !status.includes(FormStatus.INVALID);
            });
    }

    public closeDialog(): void {
        this.close.emit();
    }

    public onSelectComponent(componentName: ComponentType): void {
        this.selectedNoteComponent = componentName;
        this.pasteComponentSelected = false;
        this.formGroup?.get('selectedComponent')?.setValue(componentName);
    }

    onPasteComponent(): void {
        this.selectedNoteComponent = undefined;
        this.pasteComponentSelected = true;
        this.formGroup?.get('selectedComponent')?.setValue(this.clipboardData.componentType);
    }

    public onAddNoteAccordion(event: Event): void {
        event.stopPropagation();
        if (
            this.addedNote != null &&
            this.addedNote.title != null &&
            this.addedNote.title.trim().length > 0 &&
            (this.addedNote.selectedComponent != null || this.pasteComponentSelected)
        ) {
            if (this.pasteComponentSelected && this.clipboardData) {
                this.noteAccordionAdded.emit({
                    ...this.addedNote,
                    pastedComponent: this.clipboardData,
                });
            } else {
                this.noteAccordionAdded.emit(this.addedNote);
            }
            this.close.emit();
        }
    }
}
